import React, { useState } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

type EmailConfirmationProps = {
    email: string;
    onContinue?: () => void;
};

export const EmailConfirmation: React.FC<EmailConfirmationProps> = ({
    email,
    onContinue,
}) => {
    const [isResending, setIsResending] = useState(false);
    const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const supabase = getSupabaseClient();

    const handleResendEmail = async () => {
        setIsResending(true);
        setResendStatus('idle');

        try {
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: email,
            });

            if (error) throw error;
            setResendStatus('success');
        } catch (error) {
            console.error('Error resending confirmation email:', error);
            setResendStatus('error');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
            <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
                <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
                    <div className="w-10" aria-hidden />
                    <div className="flex flex-1 justify-center">
                        <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
                    </div>
                    <div className="w-10" aria-hidden />
                </header>
                <div className="hidden lg:block">
                    <SeekerHeader />
                </div>

                <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
                    <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
                        <div className="flex w-full flex-col gap-6">
                            <div className="text-center">
                                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#304031]/10">
                                    <svg className="h-8 w-8 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h1 className="font-recoleta text-[28px] leading-[1.1] text-[#304031]">Check your email</h1>
                                <p className="mt-3 text-sm leading-relaxed text-[#304031]/70">
                                    We've sent a confirmation link to <span className="font-semibold text-[#304031]">{email}</span>
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-[#304031]/60">
                                    Click the link in the email to verify your account and continue.
                                </p>
                            </div>

                            <div className="rounded-2xl border border-[#304031]/15 bg-white/80 p-5">
                                <h2 className="text-sm font-semibold text-[#304031]">What's next?</h2>
                                <ul className="mt-3 space-y-2 text-sm text-[#304031]/70">
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#304031]">1.</span>
                                        <span>Check your inbox (and spam folder just in case)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#304031]">2.</span>
                                        <span>Click the confirmation link in the email</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-[#304031]">3.</span>
                                        <span>It will take you back here to schedule your session.</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="flex flex-col gap-3">
                                <button
                                    type="button"
                                    className="w-full rounded-full border border-[#304031]/30 bg-[#FFFCEF] px-6 py-4 text-base font-semibold text-[#304031] transition hover:bg-[#304031]/5 disabled:pointer-events-none disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                                    onClick={handleResendEmail}
                                    disabled={isResending}
                                >
                                    {isResending ? 'Sending...' : 'Resend confirmation email'}
                                </button>

                                {resendStatus === 'success' && (
                                    <div className="rounded-2xl border border-[#4CAF50]/30 bg-[#E8F5E9] px-4 py-3 text-sm font-medium text-[#2E7D32]">
                                        Email sent! Check your inbox.
                                    </div>
                                )}

                                {resendStatus === 'error' && (
                                    <div className="rounded-2xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-4 py-3 text-sm font-medium text-[#B24E4E]">
                                        Failed to resend email. Please try again.
                                    </div>
                                )}
                            </div>

                            <div className="rounded-2xl border border-[#304031]/15 bg-white/80 px-4 py-3 text-sm text-[#304031]/70">
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="h-4 w-4 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Didn't receive the email? Check your spam folder or contact support.
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <div className="hidden lg:block">
                <Footer />
            </div>
        </div>
    );
};
